import { isInt, isNumber } from "./is";

/**
 * A `Set` with a fixed maximum capacity.
 *
 * Values are evicted in first-in-first-out (FIFO) order
 * when capacity is exceeded. This means the oldest value
 * is removed to make space.
 *
 * @template V The type of values.
 *
 * @example
 * const set = new FifoSet<number>(3);
 * set.add(1); // [ 1 ]
 * set.add(2); // [ 1, 2 ]
 * set.add(3); // [ 1, 2, 3 ]
 * set.add(4); // [ 2, 3, 4 ]
 */
export class BoundedSet<V> implements Set<V> {
  private _capacity: number;
  private _set: Set<V>;

  /**
   * Creates a new instance with an optional capacity.
   *
   * @param capacity (Optional) The maximum number of values the set can hold. Defaults to `Infinity`.
   */
  constructor(capacity?: number);
  /**
   * Creates a new instance with values from the given iterable and an optional capacity.
   *
   * @param iterable (Optional) An iterable of values to initialize the set with.
   * @param capacity (Optional) The maximum number of values the set can hold. Defaults to `Infinity`.
   */
  constructor(iterable?: Iterable<V> | null, capacity?: number);
  /**
   * Creates a new instance from an existing `Set` and an optional capacity.
   *
   * @param set (Optional) A `Set` to initialize the instance with.
   * @param capacity (Optional) The maximum number of values the set can hold. Defaults to `Infinity`.
   */
  constructor(set?: Set<V> | null, capacity?: number);
  constructor(set?: Iterable<V> | Set<V> | null | number, capacity?: number) {
    if (isNumber(set)) {
      capacity = set;
      set = undefined;
    }

    this._capacity = 0;
    if (set == null) {
      this._set = new Set();
    } else if (set instanceof Set) {
      this._set = set;
    } else {
      this._set = new Set(set);
    }

    this.capacity = capacity ?? Infinity;
  }

  /**
   * Adds a new value to the set.
   *
   * If the value already exists, it is re-inserted at the newest position.
   *
   * If capacity would be exceeded, the oldest value is removed.
   *
   * @param value The value to add.
   *
   * @returns The set instance.
   */
  add(value: V): this {
    if (!this.delete(value) && this.size >= this._capacity) {
      this.shift();
    }
    this._set.add(value);
    return this;
  }

  /**
   * The maximum number of values the set can hold.
   */
  get capacity(): number {
    return this._capacity;
  }

  /**
   * Must be a nonnegative integer or `Infinity`.
   *
   * When reduced, the oldest values are
   * evicted until the set is within capacity.
   *
   * @throws {RangeError} If an invalid capacity is given.
   */
  set capacity(value: number) {
    // Sanitize input
    if (value !== Infinity && (!isInt(value) || value < 0)) {
      throw new RangeError("Invalid set capacity");
    }

    // Update capacity
    this._capacity = value;

    // Check if size violates new capacity
    if (this.size <= this._capacity) {
      return;
    }

    // Special case; Remove all values
    if (value === 0) {
      this._set.clear();
      return;
    }

    // Reduce size to within capacity
    const iterator = this.keys();
    do {
      this.delete(iterator.next().value!);
    } while (this.size > this._capacity);
  }

  /**
   * Removes all values from the set.
   */
  clear(): void {
    this._set.clear();
  }

  /**
   * Removes a specified value from the set.
   *
   * @param value The value to remove.
   *
   * @returns `true` if the value was found and removed, otherwise `false`.
   */
  delete(value: V): boolean {
    return this._set.delete(value);
  }

  /**
   * @param other The other set.
   *
   * @param capacity (Optional) The capacity for the new set. Defaults to this set's capacity.
   *
   * @returns A new set containing the values that are in this set and not in `other`
   */
  difference<U>(other: ReadonlySetLike<U>, capacity?: number): BoundedSet<V> {
    return new BoundedSet(
      this._set.difference(other),
      capacity ?? this.capacity,
    );
  }

  /**
   * Returns an iterable of [value, value] pairs for every value in the set.
   *
   * @returns An iterable of [value, value] pairs for every value in the set.
   */
  entries(): SetIterator<[V, V]> {
    return this._set.entries();
  }

  /**
   * Executes a provided function once for each value in the set.
   *
   * @param callbackFn The function to execute.
   *
   * @param thisArg (Optional) The value to use as `this` when executing the function.
   */
  forEach(
    callbackFn: (value: V, value2: V, set: BoundedSet<V>) => void,
    thisArg?: unknown,
  ): void {
    this._set.forEach((value, value2) => {
      callbackFn.call(thisArg, value, value2, this);
    });
  }

  /**
   * Checks whether the given value exists in the set.
   *
   * @param value The value to check.
   *
   * @returns `true` if the value exists, otherwise `false`.
   */
  has(value: V): boolean {
    return this._set.has(value);
  }

  /**
   * @param other The other set.
   *
   * @param capacity (Optional) The capacity for the new set. Defaults to this set's capacity.
   *
   * @return A new set containing the values that are in both this set and `other`.
   */
  intersection<U>(
    other: ReadonlySetLike<U>,
    capacity?: number,
  ): BoundedSet<V & U> {
    return new BoundedSet<V & U>(
      this._set.intersection(other),
      capacity ?? this.capacity,
    );
  }

  /**
   * @param other The other set.
   *
   * @returns `true` if this set has no values in common with `other`, otherwise `false`.
   */
  isDisjointFrom(other: ReadonlySetLike<unknown>): boolean {
    return this._set.isDisjointFrom(other);
  }

  /**
   * @param other The other set.
   *
   * @returns `true` if all values in this set are in `other`, otherwise `false`.
   */
  isSubsetOf(other: ReadonlySetLike<unknown>): boolean {
    return this._set.isSubsetOf(other);
  }

  /**
   * @param other The other set.
   *
   * @returns `true` if all values in `other` are in this set, otherwise `false`.
   */
  isSupersetOf(other: ReadonlySetLike<unknown>): boolean {
    return this._set.isSupersetOf(other);
  }

  /**
   * Returns an iterable over the values in the set.
   *
   * @returns An iterable over the values in the set.
   */
  keys(): SetIterator<V> {
    return this._set.keys();
  }

  /**
   * Removes and returns the oldest value in the set.
   *
   * @returns The removed value, or `undefined` if the set is empty.
   */
  shift(): V | undefined {
    const iterator = this.values();
    const next = iterator.next();
    if (next.done) {
      return undefined;
    }
    this.delete(next.value);
    return next.value;
  }

  /**
   * @param other The other set.
   * @param capacity (Optional) The capacity for the new set. Defaults to this set's capacity.
   *
   * @returns A new set containing the values that are in either this set or `other`, but not both.
   */
  symmetricDifference<U>(
    other: ReadonlySetLike<U>,
    capacity?: number,
  ): BoundedSet<V | U> {
    return new BoundedSet<V | U>(
      this._set.symmetricDifference(other),
      capacity ?? this.capacity,
    );
  }

  /**
   * The number of values in the set.
   */
  get size(): number {
    return this._set.size;
  }

  /**
   * @param other The other set.
   * @param capacity (Optional) The capacity for the new set. Defaults to this set's capacity.
   *
   * @returns A new set containing all values in this set and `other`.
   */
  union<U>(other: ReadonlySetLike<U>, capacity?: number): BoundedSet<V | U> {
    return new BoundedSet<V | U>(
      this._set.union(other),
      capacity ?? this.capacity,
    );
  }

  /**
   * Returns an iterable over the values in the set.
   *
   * @returns An iterable over the values in the set.
   */
  values(): SetIterator<V> {
    return this._set.values();
  }

  /**
   * Returns an iterable over the values in the set.
   *
   * @returns An iterable over the values in the set.
   */
  [Symbol.iterator](): SetIterator<V> {
    return this._set[Symbol.iterator]();
  }

  /**
   * The default string tag.
   */
  get [Symbol.toStringTag](): string {
    return BoundedSet.name;
  }
}
